import { Button, Stack, TextField, Typography } from "@mui/material";
import { ScrollCards } from "../../../Common/components/ScrollCards";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import Panel from "@/features/Common/components/Panel";
import IconLoader from "@/features/Common/components/IconLoader";
import { convertPxToRem } from "@/utils";
import theme from "@/theme";
import ButtonWithAnalytics from "@/features/Common/components/ButtonWithAnalytics";

function CodeInputPanel() {
  const { t } = useTranslation();

  const [voucherCode, setVoucherCode] = useState("");
  const [error, setError] = useState(false);

  const voucherCodeRegex =
    /^[ABCDEFGHJKMNPQRSTUVWXYZ]{4}-[123456789]{4}-[ABCDEFGHJKMNPQRSTUVWXYZ]{4}$/;

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.toUpperCase();
    setVoucherCode(value);

    if (voucherCodeRegex.test(value) || value.length == 0) {
      setError(false);
    } else {
      setError(true);
    }
  };

  return (
    <Panel>
      <Stack spacing={2} sx={{ zIndex: 2 }}>
        <Stack
          spacing={1}
          component="button"
          sx={{
            cursor: "pointer",
            border: "none",
            background: "transparent",
          }}
        >
          <Typography textAlign="left" variant="h4">
            {t("AddOrUseVoucher.Voucher.UseVoucherTab.codeInput.title")}
          </Typography>
          <Typography
            textAlign="left"
            sx={{ fontSize: 14 }}
            variant="body1"
            color="neutral.light"
          >
            {t("AddOrUseVoucher.Voucher.UseVoucherTab.codeInput.description")}
          </Typography>
          <Typography
            textAlign="left"
            variant="body2"
            color={error && voucherCode.length > 0 ? "error" : "text.primary"}
          >
            {t("AddOrUseVoucher.Voucher.UseVoucherTab.codeInput.label")}
          </Typography>
          <TextField
            placeholder={t(
              "AddOrUseVoucher.Voucher.UseVoucherTab.codeInput.placeholder"
            )}
            variant="outlined"
            fullWidth
            value={voucherCode}
            onChange={handleInputChange}
            error={error}
            helperText={
              error &&
              voucherCode.length > 0 &&
              t("AddOrUseVoucher.Voucher.UseVoucherTab.codeInput.errorMessage")
            }
            InputProps={{
              sx: {
                "&::placeholder": {
                  fontSize: 2,
                  color: "neutral.light",
                },
              },
            }}
            sx={{
              borderColor: "neutral.light",
              borderRadius: "5px",
              fontSize: "12px",
            }}
          />
        </Stack>
      </Stack>
    </Panel>
  );
}

function SupportPanel() {
  const { t } = useTranslation();

  return (
    <Stack
      spacing={2}
      sx={{
        backgroundColor: theme.palette.primary[100],
        borderRadius: 2,
        padding: 2,
      }}
    >
      <Stack flexDirection="row" gap={1}>
        <IconLoader
          sx={{
            width: convertPxToRem(22),
            height: convertPxToRem(22),
          }}
          icon="PadlockIcon"
        />
        <Stack>
          <Typography variant="h4">
            {t("AddOrUseVoucher.Voucher.UseVoucherTab.supportPanel.title")}
          </Typography>
          <Typography
            variant="h4"
            fontWeight="400"
            sx={{ fontSize: 12 }}
            pt={1}
          >
            {t(
              "AddOrUseVoucher.Voucher.UseVoucherTab.supportPanel.description"
            )}
          </Typography>
        </Stack>
      </Stack>
      <ButtonWithAnalytics
        page="PathologyPanel"
        text={t(
          "AddOrUseVoucher.Voucher.UseVoucherTab.supportPanel.buttonText"
        )}
        intent="navigational"
        variant="outlined"
        color="neutral"
      >
        {t("AddOrUseVoucher.Voucher.UseVoucherTab.supportPanel.buttonText")}
      </ButtonWithAnalytics>
    </Stack>
  );
}

export default function UseVoucherTab() {
  const { t } = useTranslation();
  const useVoucherCardData = [
    {
      title: t("AddOrUseVoucher.Voucher.UseVoucherTab.cardOne.title"),
      description: t(
        "AddOrUseVoucher.Voucher.UseVoucherTab.cardOne.description"
      ),
      iconName: "VoucherUseIcon",
    },
    {
      title: t("AddOrUseVoucher.Voucher.UseVoucherTab.cardTwo.title"),
      description: t(
        "AddOrUseVoucher.Voucher.UseVoucherTab.cardTwo.description"
      ),
      iconName: "VoucherReceiveIcon",
    },
    {
      title: t("AddOrUseVoucher.Voucher.UseVoucherTab.cardThree.title"),
      description: t(
        "AddOrUseVoucher.Voucher.UseVoucherTab.cardThree.description"
      ),
      iconName: "VoucherApplyIcon",
    },
  ];

  return (
    <Stack spacing={1}>
      <Typography variant="h4" fontWeight={600}>
        {t("AddOrUseVoucher.Voucher.UseVoucherPage.title")}
      </Typography>
      <ScrollCards cardsData={useVoucherCardData} />
      <CodeInputPanel />
      <SupportPanel />
      <Button sx={{ marginTop: "auto" }}>Apply</Button>
    </Stack>
  );
}
